/* eslint-disable*/
import React, { useEffect, useState, useRef, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faSearch,
  faHome,
  faSync
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import moment from "moment";
import { addMonths, subMonths, startOfMonth, set } from "date-fns";
import { showToast } from "../../components/toast";
import '../../components/skeletonRow.css'

const RevenueReport = () => {
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const tableRenderedRef = useRef(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const prevMonthStart = startOfMonth(subMonths(new Date(), 1));

  // Separate state for form inputs and actual filters
  const [formInputs, setFormInputs] = useState({
    fullname: "",
    mobileno: "",
    lead_status: "",
    subscribed_on_start: null,
    subscribed_on_end: null,
    created_on_start: null,
    created_on_end: null,
    assignedto: ""
  });

  const [filters, setFilters] = useState({
    fullname: "",
    mobileno: "",
    lead_status: "",
    subscribed_on_start: "",
    subscribed_on_end: "",
    created_on_start: "",
    created_on_end: "",
    assignedto: "",
    orderbycol: "created_on",
    orderby: "desc",
    page: 1,
    limit: 100
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    showingRecords: "Showing 0 to 0 of 0 entries"
  });

  const [dropdownOptions, setDropdownOptions] = useState({
    leadStatusList: [],
    userList: []
  });

  // Refs for double scroll
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const topScrollbarContentRef = useRef(null);

  // Fetch revenue data
  const fetchRevenueData = useCallback(async (pageNumber = 1) => {
    try {
      setLoading(true);
      setIsRendering(true);
      tableRenderedRef.current = false;

      const token = localStorage.getItem("authToken");

      const filterParams = {};

      // Apply filters
      if (filters.fullname) filterParams.fullname = filters.fullname;
      if (filters.mobileno) filterParams.mobileno = filters.mobileno;
      if (filters.lead_status) filterParams.lead_status = filters.lead_status;
      if (filters.assignedto) filterParams.assignedto = filters.assignedto;

      // Date range filters
      if (filters.created_on_start) {
        filterParams.screated_on = moment(filters.created_on_start).format("YYYY-MM-DD");
      }
      if (filters.created_on_end) {
        filterParams.ecreated_on = moment(filters.created_on_end).format("YYYY-MM-DD");
      }
      if (filters.subscribed_on_start) {
        filterParams.ssubscribed_on = moment(filters.subscribed_on_start).format("YYYY-MM-DD");
      }
      if (filters.subscribed_on_end) {
        filterParams.esubscribed_on = moment(filters.subscribed_on_end).format("YYYY-MM-DD");
      }

      // Sorting
      if (filters.orderbycol) filterParams.orderbycol = filters.orderbycol;
      if (filters.orderby) filterParams.orderby = filters.orderby;

      // Pagination
      filterParams.page = pageNumber;
      filterParams.limit = filters.limit;

      const response = await axios.get(
        `${API_URL}report-revenue/revenue`,
        {
          params: filterParams,
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "*/*"
          }
        }
      );

      if (response.data && response.data.data) {
        setRevenueData(response.data.data);
        setTotalAmount(response.data.total_amount || 0);

        const totalRecords = response.data.total || response.data.data.length;
        const totalPages = Math.ceil(totalRecords / filters.limit);
        const startRecord = ((pageNumber - 1) * filters.limit) + 1;
        const endRecord = Math.min(pageNumber * filters.limit, totalRecords);

        setPagination({
          currentPage: pageNumber,
          totalPages: totalPages,
          totalRecords: totalRecords,
          showingRecords: `${startRecord} to ${endRecord} of ${totalRecords}`
        });

        // Set dropdown options from response
        if (response.data.leadStatusList || response.data.userlist) {
          setDropdownOptions({
            leadStatusList: response.data.leadStatusList || [],
            userList: response.data.userlist || []
          });
        }

        setTimeout(() => {
          tableRenderedRef.current = true;
          checkRenderingStatus();
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching revenue data:", error);
      setRevenueData([]);
      tableRenderedRef.current = true;
      checkRenderingStatus();
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const checkRenderingStatus = () => {
    if (tableRenderedRef.current) {
      setTimeout(() => {
        setIsRendering(false);
      }, 300);
    }
  };

  // Fetch dropdown data separately if needed
  const fetchDropdownData = async () => {
    try {
      const token = localStorage.getItem("authToken");

      const response = await axios.get(`${API_URL}report-chart/revenue`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "*/*"
        }
      });

      if (response.data) {
        setDropdownOptions({
          leadStatusList: response.data.leadStatusList || [],
          userList: response.data.userlist || []
        });
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  useEffect(() => {
    fetchRevenueData(filters.page);
  }, [filters, fetchRevenueData]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  // Double scroll implementation
  useEffect(() => {
    const updateScrollbar = () => {
      if (tableContainerRef.current && topScrollbarContentRef.current) {
        const tableWidth = tableContainerRef.current.scrollWidth;
        topScrollbarContentRef.current.style.width = `${tableWidth}px`;
      }
    };

    updateScrollbar();
    window.addEventListener('resize', updateScrollbar);
    return () => window.removeEventListener('resize', updateScrollbar);
  }, [revenueData]);

  const isSyncingScrollRef = useRef(false);
  const lastTableScrollLeftRef = useRef(0);

  const syncScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = tableContainerRef.current.scrollLeft;
      if (currentLeft === lastTableScrollLeftRef.current) return;
      lastTableScrollLeftRef.current = currentLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      topScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = topScrollbarRef.current.scrollLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = currentLeft;
      lastTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  // Handle form input changes (without applying filters immediately)
  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormInputs(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle date range changes for Created Date
  const handleCreatedDateChange = (dates) => {
    const [start, end] = dates;
    setFormInputs(prev => ({
      ...prev,
      created_on_start: start,
      created_on_end: end
    }));
  };

  // Handle date range changes for Subscribed On
  const handleSubscribedDateChange = (dates) => {
    const [start, end] = dates;
    setFormInputs(prev => ({
      ...prev,
      subscribed_on_start: start,
      subscribed_on_end: end
    }));
  };

  // Apply all filters (called on Enter key or search button click)
  const applyFilters = () => {
    setFilters(prev => ({
      ...prev,
      fullname: formInputs.fullname,
      mobileno: formInputs.mobileno,
      lead_status: formInputs.lead_status,
      assignedto: formInputs.assignedto,
      created_on_start: formInputs.created_on_start,
      created_on_end: formInputs.created_on_end,
      subscribed_on_start: formInputs.subscribed_on_start,
      subscribed_on_end: formInputs.subscribed_on_end,
      page: 1
    }));
  };

  // Handle Enter key in text inputs
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      applyFilters();
    }
  };

  const handleSort = (column) => {
    setFilters(prev => ({
      ...prev,
      orderbycol: column,
      orderby: prev.orderbycol === column && prev.orderby === "asc" ? "desc" : "asc",
      page: 1
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  const exportReport = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem("authToken");

      const filterParams = {};

      // Apply same filters as fetch
      if (filters.fullname) filterParams.fullname = filters.fullname;
      if (filters.mobileno) filterParams.mobileno = filters.mobileno;
      if (filters.lead_status) filterParams.lead_status = filters.lead_status;
      if (filters.assignedto) filterParams.assignedto = filters.assignedto;

      // Date range filters for export
      if (filters.created_on_start) {
        filterParams.screated_on = moment(filters.created_on_start).format("YYYY-MM-DD");
      }
      if (filters.created_on_end) {
        filterParams.ecreated_on = moment(filters.created_on_end).format("YYYY-MM-DD");
      }
      if (filters.subscribed_on_start) {
        filterParams.ssubscribed_on = moment(filters.subscribed_on_start).format("YYYY-MM-DD");
      }
      if (filters.subscribed_on_end) {
        filterParams.esubscribed_on = moment(filters.subscribed_on_end).format("YYYY-MM-DD");
      }

      const response = await axios.get(
        `${API_URL}report-revenue/revenue/export`,
        {
          params: filterParams,
          headers: {
            Authorization: `Bearer ${token}`
          },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `revenue-report-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast.success("Report exported successfully.");
    } catch (error) {
      console.error("Error exporting report:", error);
      showToast.error("Failed to export report. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const clearFilters = () => {
    setFormInputs({
      fullname: "",
      mobileno: "",
      lead_status: "",
      subscribed_on_start: null,
      subscribed_on_end: null,
      created_on_start: null,
      created_on_end: null,
      assignedto: ""
    });
    setFilters({
      fullname: "",
      mobileno: "",
      lead_status: "",
      subscribed_on_start: "",
      subscribed_on_end: "",
      created_on_start: "",
      created_on_end: "",
      assignedto: "",
      orderbycol: "created_on",
      orderby: "desc",
      page: 1,
      limit: 100
    });
  };


  useEffect(() => {
    const handleClearFilters = () => {
      clearFilters();
    };

    window.addEventListener('clearRevenueReportFiltersEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearRevenueReportFiltersEvent', handleClearFilters);
    };
  }, []);

  // Helper functions
  const getSortIcon = (column) => {
    if (filters.orderbycol !== column) return faSort;
    return filters.orderby === "asc" ? faSortUp : faSortDown;
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0.00";
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(amount));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "----";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "----";
      return moment(date).format("DD-MM-YYYY");
    } catch (error) {
      return "----";
    }
  };

  const calculateRevenuePercentage = (item) => {
    const renewalAmt = parseFloat(item.renewal_amt) || 0;
    const noOfRenewal = parseInt(item.no_of_renewal) || 0;
    const amount = parseFloat(item.amount) || 0;

    if (renewalAmt === 0 || noOfRenewal === 0 || amount === 0) {
      return "0.00";
    }

    const percentage = ((renewalAmt / noOfRenewal) / amount) * 100;
    return isNaN(percentage) ? "0.00" : percentage.toFixed(2);
  };

  const hasAnyFilter = () => {
    return (
      filters.fullname ||
      filters.mobileno ||
      filters.lead_status ||
      filters.subscribed_on_start ||
      filters.subscribed_on_end ||
      filters.created_on_start ||
      filters.created_on_end ||
      filters.assignedto ||
      filters.orderbycol !== "created_on" ||
      filters.orderby !== "desc"
    );
  };

  const isPrevDisabled = pagination.currentPage <= 1;
  const isNextDisabled = pagination.currentPage >= pagination.totalPages;

  return (
    <div className="panel-content mt-5">
      {/* {(loading || isRendering) && <LoadingSpinner />} */}

      {/* Header with Breadcrumb and Controls */}
      <div className="panel-heading d-flex justify-content-between">
        <div className="col-md-4">
          <div style={{ float: "left", position: "relative", marginLeft: "5px" }}>
            {hasAnyFilter() && (
              <button
                className="btn btn-default btn-xs ml-1"
                style={{ marginLeft: "6px" }}
                onClick={clearFilters}
                title="Clear all filters"
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>
        <div>
          <span style={{ fontSize: "0.8rem" }} className="me-3">
            {loading ? (
              <span
                className="skeleton-text"
                style={{ width: "120px", display: "inline-block" }}
              ></span>
            ) : (
              pagination.showingRecords
            )}
          </span>

          <button
            className="btn btn-default btn-sm mx-1"
            onClick={() => fetchRevenueData(1)}
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
          <button
            className="btn btn-default btn-sm mx-1"
            onClick={exportReport}
            disabled={exportLoading || revenueData.length === 0}
          >
            {exportLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Exporting...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faDownload} /> Export Report
              </>
            )}
          </button>
          <button
            className={`btn btn-default btn-sm ${filters.page === 1 ? "d-none" : ""}`}
            title="Previous"
            onClick={() => handlePageChange(filters.page - 1)}
            disabled={filters.page === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          &nbsp;
          <button
            className={`btn btn-default btn-sm ${filters.page === pagination.totalPages ? "d-none" : ""}`}
            title="Next"
            onClick={() => handlePageChange(filters.page + 1)}
            disabled={filters.page === pagination.totalPages}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>

      {/* Main Panel */}
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="large-table-container-3">
          {/* Top Scrollbar */}
          <div
            ref={topScrollbarRef}
            onScroll={handleTopScroll}
            style={{
              overflowX: "auto",
              overflowY: "hidden",
              height: "16px",
              marginBottom: "2px",
              background: "#f8f9fa",
            }}
          >
            <div
              ref={topScrollbarContentRef}
              style={{ height: "1px" }}
            ></div>
          </div>

          {/* Table Container */}
          <div
            ref={tableContainerRef}
            onScroll={syncScroll}
            style={{
              overflowY: "auto",
              height: "calc(112vh - 250px)",
              overflowX: "auto",
              position: "relative",
            }}
          >
            <table className="table table-condensed table-hover bg-white sticky-table">
              <thead>
                {/* Header Row */}
                <tr style={{ cursor: 'pointer' }}>
                  <th onClick={() => handleSort('fullname')}>
                    Name <FontAwesomeIcon icon={getSortIcon('fullname')} />
                  </th>
                  <th>Mobile No</th>
                  <th onClick={() => handleSort('lead_status')}>
                    Lead Status <FontAwesomeIcon icon={getSortIcon('lead_status')} />
                  </th>
                  <th onClick={() => handleSort('subscribed_on')}>
                    Subscribed On <FontAwesomeIcon icon={getSortIcon('subscribed_on')} />
                  </th>
                  <th onClick={() => handleSort('created_on')}>
                    Created Date <FontAwesomeIcon icon={getSortIcon('created_on')} />
                  </th>
                  <th>Client Status</th>
                  <th>First Subscription Date</th>
                  <th>First Subscription Amount</th>
                  <th>No. of Renewal</th>
                  <th>Renewal Revenue</th>
                  <th>Revenue in %</th>
                  <th onClick={() => handleSort('assigned_user')}>
                    Assigned To <FontAwesomeIcon icon={getSortIcon('assigned_user')} />
                  </th>
                </tr>

                {/* Filter Row */}
                <tr>
                  <th>
                    <input
                      type="text"
                      name="fullname"
                      placeholder="Search name..."
                      value={formInputs.fullname}
                      onChange={handleFormInputChange}
                      onKeyDown={handleKeyDown}
                      style={{
                        width: "120px",
                        padding: "5px",
                      }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="mobileno"
                      placeholder="Search mobile..."
                      value={formInputs.mobileno}
                      onChange={handleFormInputChange}
                      onKeyDown={handleKeyDown}
                      style={{
                        width: "120px",
                        padding: "5px",
                      }}
                    />
                  </th>
                  <th>
                    <select
                      name="lead_status"
                      value={formInputs.lead_status}
                      onChange={(e) => {
                        handleFormInputChange(e);
                        setFilters(prev => ({
                          ...prev,
                          lead_status: e.target.value,
                          page: 1
                        }));
                      }}
                      style={{ width: "120px", padding: "5px" }}
                    >
                      <option value="">--select--</option>
                      {dropdownOptions.leadStatusList && dropdownOptions.leadStatusList.map((item, index) => (
                        <option key={index} value={item.pick_name}>
                          {item.pick_name}
                        </option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <DatePicker
                        selected={formInputs.subscribed_on_start}
                        onChange={handleSubscribedDateChange}
                        startDate={formInputs.subscribed_on_start}
                        endDate={formInputs.subscribed_on_end}
                        selectsRange
                        isClearable
                        toggleCalendarOnIconClick
                        placeholderText=""
                        monthsShown={3}
                        openToDate={formInputs.subscribed_on_start || new Date()}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        scrollableYearDropdown
                        yearDropdownItemNumber={15}
                        minDate={new Date(2018, 0, 1)}
                        maxDate={new Date(2030, 11, 31)}
                        dateFormat="dd-MM-yyyy"
                      />
                    </div>
                  </th>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <DatePicker
                        selected={formInputs.created_on_start}
                        onChange={handleCreatedDateChange}
                        startDate={formInputs.created_on_start}
                        endDate={formInputs.created_on_end}
                        selectsRange
                        isClearable
                        toggleCalendarOnIconClick
                        placeholderText=""
                        monthsShown={3}
                        openToDate={formInputs.created_on_start || new Date()}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        scrollableYearDropdown
                        yearDropdownItemNumber={15}
                        minDate={new Date(2018, 0, 1)}
                        maxDate={new Date(2030, 11, 31)}
                        dateFormat="dd-MM-yyyy"
                      />
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-group-xs"
                        onClick={applyFilters}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th>
                    <select
                      name="assignedto"
                      value={formInputs.assignedto}
                      onChange={(e) => {
                        handleFormInputChange(e);
                        setFilters(prev => ({
                          ...prev,
                          assignedto: e.target.value,
                          page: 1
                        }));
                      }}
                      style={{ width: "120px", padding: "5px" }}
                    >
                      <option value="">--select--</option>
                      {dropdownOptions.userList && dropdownOptions.userList.map((user, index) => (
                        <option key={index} value={user.id}>
                          {user.user_fullname}
                        </option>
                      ))}
                    </select>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(loading || isRendering) ? (
                  // Skeleton loading rows (initial load + filter apply)
                  Array.from({ length: 10 }).map((_, skeletonIndex) => (
                    <tr key={`skeleton-${skeletonIndex}`} className="skeleton-row">
                      <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "75%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "50%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "50%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                    </tr>
                  ))
                ) : revenueData.length > 0 ? (
                  revenueData.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <a
                          href={`/subscriber/edit/${item.id}`}
                          target="_blank"
                          style={{ textDecoration: 'none' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {item.fullname}
                        </a>
                      </td>
                      <td>{item.mobileno}</td>
                      <td>{item.lead_status}</td>
                      <td>{formatDate(item.subscribed_on)}</td>
                      <td>{formatDate(item.created_on)}</td>
                      <td>{item.client_status}</td>
                      <td>{formatDate(item.first_subscription_date)}</td>
                      <td style={{ textAlign: "right" }}>
                        <strong>₹{formatCurrency(item.amount)}</strong>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {item.no_of_renewal}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {item.renewal_amt ? `₹${formatCurrency(item.renewal_amt)}` : '-'}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {calculateRevenuePercentage(item)}%
                      </td>
                      <td>{item.assigned_user}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    {/* No records state kept unchanged */}
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: "bold", background: "#f8f9fa" }}>
                  <td colSpan={7} style={{ textAlign: "right" }}>
                    Total
                  </td>

                  {/* First Subscription Amount column */}
                  <td style={{ textAlign: "right" }}>
                    ₹{formatCurrency(totalAmount)}
                  </td>

                  {/* Remaining columns */}
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>

            </table>
          </div>
        </div>
      </div>

      <style jsx>{`
        .breadcrumb {
          background: transparent;
          margin-bottom: 0;
          padding: 0;
        }

        .breadcrumb-item + .breadcrumb-item::before {
          content: ">";
        }

        .panel {
          background: white;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .with-rounded-corner {
          border-radius: 4px;
        }

        .with-shadow {
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .table-condensed th,
        .table-condensed td {
          padding: 8px 5px;
          font-size: 12px;
        }

        .btn-default {
          background: #f8f9fa;
          border: 1px solid #ddd;
        }

        .btn-xs {
          padding: 1px 5px;
          font-size: 12px;
          line-height: 1.5;
        }

        .form-control.input-sm {
          height: 28px;
          padding: 3px 6px;
          font-size: 12px;
        }

        th {
          white-space: nowrap;
          background: #f8f9fa;
        }

        .table-hover tbody tr:hover {
          background-color: #f5f5f5;
        }

        .badge {
          font-size: 0.7em;
        }
      `}</style>
    </div>
  );
};

export default RevenueReport;